import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "@material-tailwind/react";
import { ArrowLeftIcon, CheckCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate, useParams } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import FormField from "../components/FormField";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { addProjectPayment, addProjectTask, deleteProjectPayment, deleteProjectTask, subscribeByOwner, subscribeProject, toggleProjectTask } from "../services/db";
import { formatMoneyWithUnit, toEnglishDigits } from "../utils/format";
import { calculateProjectProgress } from "../utils/report";

export default function ProjectDetailsPage() {
  const { user } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [taskText, setTaskText] = useState("");
  const [paymentForm, setPaymentForm] = useState({ amount: "", paymentDate: new Date().toISOString().slice(0, 10), note: "" });
  const [alert, setAlert] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user?.uid || !projectId) return undefined;
    const unsubscribers = [
      subscribeProject(projectId, setProject, () => navigate("/projects", { replace: true })),
      subscribeByOwner("projectTasks", user.uid, setTasks),
      subscribeByOwner("projectPayments", user.uid, setPayments)
    ];
    const timer = setTimeout(() => setReady(true), 250);
    return () => {
      unsubscribers.forEach((unsub) => unsub?.());
      clearTimeout(timer);
    };
  }, [navigate, projectId, user?.uid]);

  const projectTasks = useMemo(() => tasks.filter((item) => item.projectId === projectId), [projectId, tasks]);
  const projectPayments = useMemo(() => payments.filter((item) => item.projectId === projectId).sort((a, b) => new Date(b.paymentDate || 0) - new Date(a.paymentDate || 0)), [payments, projectId]);
  const progress = useMemo(() => calculateProjectProgress(projectTasks), [projectTasks]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!taskText.trim()) return;
    try {
      await addProjectTask({ ownerUid: user.uid, projectId, text: taskText });
      setTaskText("");
      setAlert({ color: "green", message: "تمت إضافة مهمة جديدة." });
    } catch {
      setAlert({ color: "red", message: "تعذر إضافة المهمة." });
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount) return;
    try {
      await addProjectPayment({ ownerUid: user.uid, projectId, amount: paymentForm.amount, paymentDate: paymentForm.paymentDate, note: paymentForm.note });
      setPaymentForm({ amount: "", paymentDate: new Date().toISOString().slice(0, 10), note: "" });
      setAlert({ color: "green", message: "تم تسجيل الدفعة بنجاح." });
    } catch (err) {
      setAlert({ color: "red", message: err?.message || "تعذر تسجيل الدفعة." });
    }
  };

  if (!ready || !project) return <LoadingScreen text="جارٍ تحميل تفاصيل المشروع..." />;

  return (
    <div className="page-shell space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/projects" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-brand-700 shadow-sm">
          <ArrowLeftIcon className="h-4 w-4" />رجوع إلى المشاريع
        </Link>
        <div>
          <h1 className="text-xl font-extrabold text-brand-800 sm:text-2xl">{project.title}</h1>
          <p className="text-sm text-slate-500">{project.clientName || "بدون عميل"}</p>
        </div>
      </div>

      {alert ? <Alert color={alert.color} className="rounded-2xl text-sm">{alert.message}</Alert> : null}

      <section className="mini-grid">
        <StatCard title="القيمة الكلية" value={formatMoneyWithUnit(project.totalValue)} tone="brand" />
        <StatCard title="المبلغ المستلم" value={formatMoneyWithUnit(project.receivedAmount)} tone="success" />
        <StatCard title="المتبقي" value={formatMoneyWithUnit(project.remainingAmount)} tone="accent" />
        <StatCard title="إنجاز المهام" value={`${toEnglishDigits(progress)}%`} tone="danger" />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <SectionCard title="مهام المشروع" subtitle="أضف الفقرات والمهام وضع علامة عليها عند اكتمالها">
          <form onSubmit={addTask} className="mb-4 flex flex-col gap-3 sm:flex-row">
            <div className="flex-1"><FormField label="مهمة جديدة" placeholder="مثال: تسليم النسخة الأولى" value={taskText} onChange={(e) => setTaskText(e.target.value)} /></div>
            <Button type="submit" className="rounded-2xl bg-brand-700"><span className="flex items-center gap-2"><PlusIcon className="h-4 w-4" />إضافة</span></Button>
          </form>

          {!projectTasks.length ? (
            <EmptyState title="لا توجد مهام" description="ابدأ بإضافة أول فقرة أو مهمة للمشروع." />
          ) : (
            <div className="space-y-3">
              {projectTasks.map((item) => (
                <div key={item.id} className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" className="flex items-center gap-3 text-right" onClick={() => toggleProjectTask(item.id, !item.completed)}>
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${item.completed ? "border-emerald-200 bg-emerald-50 text-income" : "border-slate-300 bg-white text-slate-400"}`}><CheckCircleIcon className="h-5 w-5" /></span>
                    <span className={`font-bold ${item.completed ? "text-slate-400 line-through" : "text-slate-800"}`}>{item.text}</span>
                  </button>
                  <Button variant="text" color="red" size="sm" className="flex items-center gap-2 rounded-xl" onClick={() => deleteProjectTask(item.id)}><TrashIcon className="h-4 w-4" />حذف</Button>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="دفعات المشروع" subtitle="كل دفعة تحدّث المستلم والمتبقي تلقائياً">
          <form onSubmit={addPayment} className="mb-4 space-y-4">
            <div className="form-grid">
              <FormField type="number" inputMode="decimal" dir="ltr" label="قيمة الدفعة" placeholder="0" value={paymentForm.amount} onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))} required />
              <FormField type="date" dir="ltr" label="تاريخ الدفعة" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))} required />
              <div className="sm:col-span-2 xl:col-span-3"><FormField as="textarea" label="ملاحظة" placeholder="اختياري" value={paymentForm.note} onChange={(e) => setPaymentForm((prev) => ({ ...prev, note: e.target.value }))} /></div>
            </div>
            <Button type="submit" className="rounded-2xl bg-brand-700"><span className="flex items-center gap-2"><PlusIcon className="h-4 w-4" />إضافة دفعة</span></Button>
          </form>

          {!projectPayments.length ? (
            <EmptyState title="لا توجد دفعات" description="أضف أول دفعة للمشروع لتحديث المستلم والمتبقي." />
          ) : (
            <div className="space-y-3">
              {projectPayments.map((payment) => (
                <div key={payment.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-lg font-extrabold text-income">{formatMoneyWithUnit(payment.amount)}</div>
                      <div className="mt-1 text-sm text-slate-500">التاريخ: {payment.paymentDate || "—"}</div>
                      {payment.note ? <p className="mt-2 text-sm leading-7 text-slate-600">{payment.note}</p> : null}
                    </div>
                    <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-red-200 text-red-600" onClick={() => deleteProjectPayment(projectId, payment.id)}><TrashIcon className="h-4 w-4" />حذف الدفعة</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </section>

      <SectionCard title="تفاصيل المشروع" subtitle="وصف عام ومواعيد المشروع">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 p-4"><div className="text-xs text-slate-500">الحالة</div><div className="mt-1 font-extrabold text-brand-800">{project.status === "completed" ? "مكتمل" : project.status === "on-hold" ? "معلق" : "نشط"}</div></div>
          <div className="rounded-2xl bg-slate-50 p-4"><div className="text-xs text-slate-500">تاريخ البداية</div><div className="mt-1 font-extrabold text-brand-800">{project.startDate || "—"}</div></div>
          <div className="rounded-2xl bg-slate-50 p-4"><div className="text-xs text-slate-500">الموعد المتوقع</div><div className="mt-1 font-extrabold text-brand-800">{project.dueDate || "—"}</div></div>
        </div>
        {project.description ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4"><h3 className="mb-2 font-extrabold text-slate-800">وصف المشروع</h3><p className="text-sm leading-8 text-slate-600">{project.description}</p></div>
        ) : null}
      </SectionCard>
    </div>
  );
}
